import { PagingMeta } from "@/lib/paging"
import { Timestamp } from "./date"
import { FavoritePlace, PlaceType } from "./place"
import { PublicUser, UserRef } from "./user"

// undefined is the default: split view opens the map when there are places.
// "list" records that the map was closed.
export type CollectionView = "map" | "list" | undefined
export type CollectionRef = `co-${string}`
export type CollectionFilterType = PlaceType | "all"
const CollectionRefRE = /^co-[a-zA-Z0-9]{8}$/

export interface CollectionSummary {
  updated_at: Timestamp
  ref: CollectionRef
  name: string
  favorites_count: number
  public: boolean
  images: string[]
  canonical_url: string
  owner: PublicUser
  accepted_collaborator_refs?: UserRef[]
  refreshable: boolean
  // number of users who saved this collection; in a summary, only sent to its owner
  favorite_collections_count?: number
}

export interface DetailedCollection extends CollectionSummary {
  favorites: FavoritePlace[]
  collaboration?: CollectionCollaboration
  // number of users who saved this collection
  favorite_collections_count: number
}

export type Collection = CollectionSummary | DetailedCollection

// A public collection the current user saved without owning or collaborating on it
export interface FavoriteCollection {
  collection_ref: CollectionRef
  updated_at: Timestamp
}

export interface CollectionResponse {
  data: DetailedCollection
  meta: PagingMeta
}

export type CollectionTrigger = "icon" | "page" | "import"
export type CollectionImportType = "file" | "link" | "google"

export type CollectionCollaboratorRef = `cc-${string}`

export type CollectionSSRProps = {
  collection: DetailedCollection
  meta: PagingMeta
}

export type CollectionEmbeddedProps = {
  collectionRef: CollectionRef
}

export type CollectionProps = CollectionSSRProps | CollectionEmbeddedProps

export interface EmailCollaborator {
  invited_email: string
}

export interface UserCollaborator {
  user: PublicUser
}

export interface CollectionCollaborator {
  ref: CollectionCollaboratorRef
  collaborator: EmailCollaborator | UserCollaborator
  status: "pending" | "accepted"
  invited_at: Timestamp
  accepted_at?: Timestamp
}

// helper functions to work with the oneOf structure
export function isUserCollaborator(
  collaborator: EmailCollaborator | UserCollaborator
): collaborator is UserCollaborator {
  return collaborator && "user" in collaborator
}

export function isEmailCollaborator(
  collaborator: EmailCollaborator | UserCollaborator
): collaborator is EmailCollaborator {
  return collaborator && "invited_email" in collaborator
}

export interface CollectionInvitation {
  ref: CollectionCollaboratorRef
  collection: CollectionSummary
  status: "pending" | "accepted"
  invited_at: Timestamp
  accepted_at?: Timestamp
}

export interface CollectionInvitationPublicInfo {
  name?: string
  owner_name?: string
  images?: string[]
}

export interface CollaborationStats {
  total_count: number
  pending_count: number
  accepted_count: number
}

export interface CollaborationSharing {
  enabled: boolean
  url?: string
  view_link_url?: string
}

export interface CollaborationPermissions {
  can_add_remove_places: boolean
  can_manage_collaborators: boolean
  can_edit_collection_properties: boolean
}

export interface CollectionCollaboration {
  collaborators: CollectionCollaborator[]
  stats: CollaborationStats
  sharing?: CollaborationSharing
  permissions: CollaborationPermissions
}

export function isCollectionRef(value: string): value is CollectionRef {
  return CollectionRefRE.test(value)
}

export function entityIsCollection(
  entity: Collection | undefined
): entity is Collection {
  const entityRef = entity?.ref
  return entityRef ? isCollectionRef(entityRef) : false
}
